import { ref, readonly, computed, onUnmounted } from 'vue';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

/**
 * ServerAuthoritativeTimer - Client-side timer with server synchronization
 *
 * This class implements a robust timer system that maintains accuracy by
 * syncing with server time while providing smooth local updates.
 *
 * Key features:
 * - Server-authoritative timing (server time is source of truth)
 * - Local prediction for smooth UI updates
 * - Automatic drift correction when sync messages received
 * - Graceful degradation if server sync is lost
 * - High-resolution updates (100ms) for smooth animations
 */
export class ServerAuthoritativeTimer {
  private serverStartTime: number = 0;
  private duration: number = 0;
  private localStartTime: number = 0;
  private syncOffset: number = 0;
  private updateInterval: number | null = null;
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private pausedTimeRemaining: number = 0;
  private expiryCallbacks: (() => void)[] = [];
  private hasExpired: boolean = false;

  constructor(private onUpdate: (timeRemaining: number) => void) {}

  /**
   * Start the timer with server-provided timing data
   * @param serverStartTime - Server timestamp when timer started (ms)
   * @param duration - Timer duration in milliseconds
   */
  start(serverStartTime: number, duration: number): void {
    this.serverStartTime = serverStartTime;
    this.duration = duration;
    this.localStartTime = Date.now();
    // Offset between local and server clocks. Positive means server started
    // before client (reconnection). Negative means client clock is behind
    // server (clock skew). Both are valid for accurate time calculation;
    // calculateTimeRemaining() clamps the final result to 0.
    this.syncOffset = this.localStartTime - serverStartTime;
    this.isRunning = true;
    this.hasExpired = false;

    console.log('Timer started:', {
      serverStartTime: new Date(serverStartTime).toISOString(),
      duration: `${duration}ms`,
      localStartTime: new Date(this.localStartTime).toISOString(),
    });

    this.startLocalTimer();
  }

  /**
   * Synchronize with server time to correct for clock drift
   * @param serverTime - Current server timestamp
   * @param timeRemaining - Time remaining according to server (ms)
   */
  syncWithServer(serverTime: number, timeRemaining: number): void {
    if (!this.isRunning) return;

    const expectedTimeRemaining = this.calculateExpectedTime();
    const drift = Math.abs(timeRemaining - expectedTimeRemaining);

    // Only adjust if drift is significant (>1 second).
    // Replace syncOffset entirely -- the server's timeRemaining already
    // accounts for all elapsed time, including any initial reconnection gap.
    if (drift > 1000) {
      getMetrics().gameTimerDesyncDetected.add(1);
      this.syncOffset = expectedTimeRemaining - timeRemaining;
      console.log('Timer resynced with server:', {
        drift: `${drift}ms`,
        expectedTime: `${expectedTimeRemaining}ms`,
        serverTime: `${timeRemaining}ms`,
        adjustment: `${this.syncOffset}ms`,
      });
    }
  }

  /**
   * Pause the timer while preserving current time remaining
   */
  pause(): void {
    if (!this.isRunning || this.isPaused) return;

    // Calculate current time remaining BEFORE setting paused state
    this.pausedTimeRemaining = this.calculateTimeRemaining();
    this.isPaused = true;

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    console.log('Timer paused:', {
      timeRemaining: `${this.pausedTimeRemaining}ms`,
    });
  }

  /**
   * Resume the timer from where it was paused
   */
  resume(): void {
    if (!this.isRunning || !this.isPaused) return;

    this.isPaused = false;
    // Adjust timing to account for pause
    this.localStartTime = Date.now();
    this.duration = this.pausedTimeRemaining;
    this.syncOffset = 0;

    console.log('Timer resumed:', {
      timeRemaining: `${this.pausedTimeRemaining}ms`,
    });

    this.startLocalTimer();
  }

  /**
   * Stop the timer and clean up
   */
  stop(): void {
    this.isRunning = false;
    this.isPaused = false;
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Final update with exactly 0 time remaining
    this.onUpdate(0);
  }

  /**
   * Get current time remaining
   */
  getTimeRemaining(): number {
    return this.calculateTimeRemaining();
  }

  /**
   * Check if timer is currently running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Check if timer is currently paused
   */
  get paused(): boolean {
    return this.isPaused;
  }

  /**
   * Register a callback to be executed when timer expires (remaining reaches 0 or below)
   * @param callback - Function to execute on timer expiry
   */
  onTimerExpiry(callback: () => void): void {
    this.expiryCallbacks.push(callback);
  }

  /**
   * Remove all expiry callbacks
   */
  clearExpiryCallbacks(): void {
    this.expiryCallbacks = [];
  }

  /**
   * Clear the update interval and mark the timer as stopped.
   * Does NOT call onUpdate(0) — callers handle that themselves.
   */
  private cleanupInterval(): void {
    this.isRunning = false;
    this.isPaused = false;
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Calculate expected time remaining based on local clock
   */
  private calculateExpectedTime(): number {
    const elapsed = Date.now() - this.localStartTime;
    return Math.max(0, this.duration - elapsed);
  }

  /**
   * Calculate actual time remaining with server sync adjustment
   */
  private calculateTimeRemaining(): number {
    if (this.isPaused) {
      return this.pausedTimeRemaining;
    }
    const elapsed = Date.now() - this.localStartTime + this.syncOffset;
    return Math.max(0, this.duration - elapsed);
  }

  /**
   * Start the local update loop
   */
  private startLocalTimer(): void {
    // Update every 100ms for smooth animations without excessive re-renders
    this.updateInterval = window.setInterval(() => {
      const remaining = this.calculateTimeRemaining();

      // Fire expiry callbacks BEFORE onUpdate(0) so callbacks can
      // observe pre-expiry state from the onUpdate consumer
      if (remaining <= 0 && !this.hasExpired) {
        this.hasExpired = true;
        this.triggerExpiryCallbacks();
      }

      this.onUpdate(remaining);

      if (remaining <= 0) {
        this.cleanupInterval();
      }
    }, 100);

    // Immediate first update — fire expiry BEFORE onUpdate, same order as
    // the interval path, so callbacks observe pre-expiry reactive state
    const initialRemaining = this.calculateTimeRemaining();

    if (initialRemaining <= 0 && !this.hasExpired) {
      this.hasExpired = true;
      this.triggerExpiryCallbacks();
    }

    this.onUpdate(initialRemaining);

    if (initialRemaining <= 0) {
      this.cleanupInterval();
    }
  }

  /**
   * Execute all registered expiry callbacks
   */
  private triggerExpiryCallbacks(): void {
    this.expiryCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        reportClientError(error, ErrorLocation.TimerExpiryCallback);
      }
    });
  }
}

/**
 * Vue composable for server-authoritative timer functionality
 *
 * Provides reactive timer state with server synchronization capabilities.
 * Integrates with Vue's reactivity system and handles cleanup automatically.
 */
export function useServerAuthoritativeTimer() {
  // Reactive timer state
  const timeRemaining = ref<number>(0);
  const isActive = ref<boolean>(false);
  const isPaused = ref<boolean>(false);
  const serverSynced = ref<boolean>(true);
  const phase = ref<'starting' | 'active' | 'finalCountdown' | 'ended'>('starting');

  // Timer instance
  let timer: ServerAuthoritativeTimer | null = null;

  /**
   * Computed properties for UI display
   */
  const displaySeconds = computed(() => Math.ceil(timeRemaining.value / 1000));
  const progressPercentage = computed(() => {
    if (!timer) return 0;
    const totalDuration = 30000; // 30 seconds in ms
    return (timeRemaining.value / totalDuration) * 100;
  });

  const isUrgent = computed(() => isActive.value && timeRemaining.value <= 10000); // Final 10 seconds
  const isCritical = computed(() => isActive.value && timeRemaining.value <= 5000); // Final 5 seconds

  /**
   * Color class for UI styling based on time remaining
   */
  const colorClass = computed(() => {
    if (timeRemaining.value > 20000) return 'timer-success'; // Green 30-21s
    if (timeRemaining.value > 10000) return 'timer-warning'; // Amber 20-11s
    if (timeRemaining.value > 5000) return 'timer-error'; // Red 10-6s
    return 'timer-critical'; // Pulsing red 5-0s
  });

  /**
   * Update timer phase based on time remaining
   */
  const updatePhase = (remaining: number) => {
    if (remaining <= 0) {
      phase.value = 'ended';
    } else if (remaining <= 10000) {
      phase.value = 'finalCountdown';
    } else {
      phase.value = 'active';
    }
  };

  /**
   * Start timer with server data
   * @param serverStartTime - Server timestamp when timer started (ms)
   * @param duration - Timer duration in milliseconds
   * @param onExpiry - Optional callback registered before timer starts,
   *                   ensuring it's in place even for immediate expiry
   */
  const startTimer = (serverStartTime: number, duration: number, onExpiry?: () => void) => {
    // Clean up existing timer
    if (timer) {
      timer.stop();
      timer.clearExpiryCallbacks();
    }

    // Create new timer instance
    timer = new ServerAuthoritativeTimer((remaining: number) => {
      timeRemaining.value = remaining;
      isPaused.value = timer?.paused ?? false;
      updatePhase(remaining);

      // Update isActive state - timer is inactive when it naturally expires
      if (remaining <= 0 && isActive.value) {
        isActive.value = false;
      }
    });

    // Register expiry callback BEFORE start so it's in place for immediate expiry
    if (onExpiry) {
      timer.onTimerExpiry(onExpiry);
    }

    // Set reactive state BEFORE start() so that synchronous onUpdate(0)
    // can correctly transition isActive back to false on immediate expiry
    isActive.value = true;
    serverSynced.value = true;
    phase.value = 'starting';

    // Start the timer (may fire callbacks synchronously for immediate expiry)
    timer.start(serverStartTime, duration);
  };

  /**
   * Sync with server time
   */
  const syncWithServer = (serverTime: number, remaining: number) => {
    if (timer) {
      timer.syncWithServer(serverTime, remaining);
      serverSynced.value = true;
    }
  };

  /**
   * Pause timer
   */
  const pauseTimer = () => {
    if (timer) {
      timer.pause();
      isPaused.value = timer.paused;
    }
  };

  /**
   * Resume timer
   */
  const resumeTimer = () => {
    if (timer) {
      timer.resume();
      isPaused.value = timer.paused;
    }
  };

  /**
   * Stop timer
   */
  const stopTimer = () => {
    if (timer) {
      timer.stop();
      timer = null;
    }
    isActive.value = false;
    isPaused.value = false;
    phase.value = 'ended';
  };

  /**
   * Handle server sync loss
   */
  const handleSyncLoss = () => {
    serverSynced.value = false;
    console.warn('Server sync lost - timer continuing with local time');
  };

  /**
   * Cleanup on component unmount
   */
  onUnmounted(() => {
    if (timer) {
      timer.stop();
    }
  });

  return {
    // Reactive state
    timeRemaining: readonly(timeRemaining),
    isActive: readonly(isActive),
    isPaused: readonly(isPaused),
    serverSynced: readonly(serverSynced),
    phase: readonly(phase),

    // Computed properties
    displaySeconds: readonly(displaySeconds),
    progressPercentage: readonly(progressPercentage),
    isUrgent: readonly(isUrgent),
    isCritical: readonly(isCritical),
    colorClass: readonly(colorClass),

    // Methods
    startTimer,
    pauseTimer,
    resumeTimer,
    syncWithServer,
    stopTimer,
    handleSyncLoss,
  };
}

/**
 * Type definitions for external use
 */
export type TimerPhase = 'starting' | 'active' | 'finalCountdown' | 'ended';

export interface TimerState {
  timeRemaining: number;
  isActive: boolean;
  isPaused: boolean;
  serverSynced: boolean;
  phase: TimerPhase;
  displaySeconds: number;
  progressPercentage: number;
  isUrgent: boolean;
  isCritical: boolean;
  colorClass: string;
}
